// One skill (D62): its details and its files. Saving files replaces the whole set; versions
// already published keep the copy they froze. Archiving hides it from drafts; restoring it
// makes it attachable again.
import { Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { Button } from "rizzui";

import { usePermissions } from "../auth/permissions";
import { useT } from "../i18n";
import { toast } from "../layout/toast";
import { archiveSkill, replaceSkillFiles, type SkillDetail, type SkillMeta, skillQuery, updateSkill } from "../skills/api";
import { FilesEditor } from "../skills/FilesEditor";
import { SkillForm } from "../skills/SkillForm";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { ErrorState, Loading } from "../ui/states";
import { Mono, Pill } from "../ui/status";
import { detailCrumbs } from "../layout/nav";

export default function SkillEditorPage() {
  const t = useT().skills.editor;
  const { skillId = "" } = useParams();
  const query = useQuery(skillQuery(skillId));
  if (query.isError) return <ErrorState title={t.loadError} message={query.error.message} />;
  if (!query.data) return <Loading label={t.loading} />;
  return <Editor key={skillId} detail={query.data} />;
}

function Editor({ detail }: { detail: SkillDetail }) {
  const messages = useT();
  const s = messages.skills;
  const t = s.editor;
  const { skill, files } = detail;
  const { can } = usePermissions();
  const manage = can("ManageSkills");
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["skills"] });
  const meta: SkillMeta = { name: skill.name, slug: skill.slug, description: skill.description };

  async function saveMeta(next: SkillMeta) {
    await updateSkill(skill.id, next);
    await refresh();
    toast(t.saved(next.name));
  }

  async function setArchived(archived: boolean) {
    try {
      if (archived) await archiveSkill(skill.id);
      else await updateSkill(skill.id, meta, "active");
      await refresh();
      toast(archived ? t.archivedToast(skill.name) : t.restoredToast(skill.name));
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "warn");
    }
  }

  return (
    <>
      <PageHeader
        title={skill.name}
        breadcrumb={detailCrumbs("skills", "/skills", { name: skill.name })}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {skill.status === "active" ? <Pill tone="green">{s.active}</Pill> : <Pill tone="gray">{s.archived}</Pill>}
            <Mono>.claude/skills/{skill.slug}/</Mono>
            <span>{t.usedBy(skill.used_by_versions)}</span>
          </span>
        }
      >
        {manage ? (
          skill.status === "active" ? (
            <Button variant="outline" className="gap-1.5" onClick={() => void setArchived(true)}>
              <Archive className="h-4 w-4" /> {t.archive}
            </Button>
          ) : (
            <Button variant="outline" className="gap-1.5" onClick={() => void setArchived(false)}>
              <ArrowCounterClockwise className="h-4 w-4" /> {t.restore}
            </Button>
          )
        ) : null}
      </PageHeader>
      <div className="grid gap-5 xl:grid-cols-12">
        <div className="xl:col-span-9">
          <FilesEditor
            initial={files.map((f) => ({ path: f.path, content: f.content }))}
            readOnly={!manage}
            onSave={async (next) => {
              await replaceSkillFiles(skill.id, next);
              await refresh();
              toast(t.filesSaved);
            }}
          />
        </div>
        <section className={cn(cardClass, "p-5 xl:col-span-3 xl:self-start")} aria-label={t.details}>
          <h3 className="mb-4 text-base font-semibold text-gray-900">{t.details}</h3>
          {manage ? <SkillForm initial={meta} submitLabel={t.saveDetails} onSubmit={saveMeta} /> : <p className="text-[13px] text-gray-600">{skill.description || t.noDescription}</p>}
        </section>
      </div>
    </>
  );
}
